import type { ChatDownloadMessage } from "@albertine/types";
import { isEmbeddedNativeWebView } from "@albertine/wrapper-interop";
import {
    isEmbeddedInParentFrame,
    parentOriginFromLocation,
    postMessageToParentFrame,
} from "@/lib/parentFrame";

const REVOKE_BLOB_DELAY_MS = 60_000;

type FileAttachment = {
    url: string;
    filename?: string;
    mimeType?: string;
};

export function downloadFileAttachment({
    url,
    filename,
    mimeType,
}: FileAttachment): void {
    // This frame must never navigate itself: iOS allows sub-frame navigation,
    // which would swap the chat for the raw file. Remote files go to Concierge,
    // which owns the top frame. Blob URLs stay here — they are origin-bound to
    // this frame.
    if (isEmbeddedInParentFrame() && isHttpUrl(url)) {
        const message: ChatDownloadMessage = {
            type: "chat.download",
            url,
            filename,
            mimeType,
        };
        postMessageToParentFrame(parentOriginFromLocation(), message);
        return;
    }

    // Embedded straight into a native WebView, so this frame is the top frame.
    // Neither SDK registers a download handler, but both hand a top-frame
    // navigation to a foreign host over to the system browser.
    if (isEmbeddedNativeWebView() && isHttpUrl(url)) {
        window.location.assign(url);
        return;
    }

    void downloadInBrowser(url, filename);
}

function isHttpUrl(url: string): boolean {
    try {
        const protocol = new URL(url).protocol;
        return protocol === "https:" || protocol === "http:";
    } catch {
        return false;
    }
}

async function downloadInBrowser(url: string, filename?: string): Promise<void> {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        triggerAnchorDownload(blobUrl, filename);
        // Revoking while the download is still starting cancels it.
        setTimeout(() => URL.revokeObjectURL(blobUrl), REVOKE_BLOB_DELAY_MS);
    } catch {
        triggerAnchorDownload(url, filename);
    }
}

function triggerAnchorDownload(href: string, filename?: string): void {
    const link = document.createElement("a");
    link.href = href;
    link.download = filename ?? "download";
    link.rel = "noopener noreferrer";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
}
